/**
 * One-time migration from localStorage blob format to IndexedDB normalized tables.
 * Reads legacy keys, writes to IndexedDB. Does not delete localStorage (safe rollback).
 */

import type { GrindModeTableBackend } from "./idb-backend";
import {
  SETTING_KEYS,
  USER_PREF_KEYS,
  type ListMemberType,
} from "./schema";

const LEGACY_KEYS = {
  tableZoom: "table-zoom",
  autoResizeTable: "autoResizeTable",
  timeMaxLate: "timeMaxLate",
  autoAddMaxLate: "autoAddMaxLate",
  refreshSchedules: "refresh-schedules",
  tourColumnsSaved: "tourColumnsSaved-grind",
  hourGroupColumn: "hourGroupColumn",
  gridColumnState: "grindColState",
  currencyRatesCache: "currency_rates_cache",
} as const;

const LEGACY_USER_SUFFIX: Record<string, string> = {
  tableSize: "tableSize2",
  playList: "playList2",
  skipList: "skipList2",
  bustLate: "bustLate2",
  hiddenList: "hiddenList2",
  removedBustLate: "bustLateRemoved2",
  isSimpleMode: "isSimpleMode2",
  grindSession: "grindSession",
};

const LIST_SUFFIX_TO_TYPE: Record<string, ListMemberType> = {
  skipList2: "skip",
  bustLate2: "bustLate",
  hiddenList2: "hidden",
  bustLateRemoved2: "removedBustLate",
};

const MIGRATION_FLAG = "grind-mode-migrated-v1";

function getLegacyKey(userId: number | string, suffix: string): string {
  return `${userId}${suffix}`;
}

function parseJson<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function getLegacySetting(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function getLegacyUserKey(userId: number | string, suffix: string): string | null {
  try {
    return window.localStorage.getItem(getLegacyKey(userId, suffix));
  } catch {
    return null;
  }
}

/** Collect all userIds that have data in localStorage */
function findLegacyUserIds(): number[] {
  const userIds = new Set<number>();
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key) continue;
      for (const suffix of Object.values(LEGACY_USER_SUFFIX)) {
        if (key.endsWith(suffix) && key !== suffix) {
          const prefix = key.slice(0, -suffix.length);
          const id = parseInt(prefix, 10);
          if (!isNaN(id)) userIds.add(id);
        }
      }
    }
  } catch {
    /* ignore */
  }
  return Array.from(userIds);
}

export function needsMigration(): boolean {
  try {
    return window.localStorage.getItem(MIGRATION_FLAG) !== "1";
  } catch {
    return false;
  }
}

export function markMigrated(): void {
  try {
    window.localStorage.setItem(MIGRATION_FLAG, "1");
  } catch {
    /* ignore */
  }
}

export async function migrateFromLocalStorage(
  backend: GrindModeTableBackend
): Promise<void> {
  if (!needsMigration()) return;

  // Settings
  const settingMap: Record<string, string> = {
    [SETTING_KEYS.tableZoom]: LEGACY_KEYS.tableZoom,
    [SETTING_KEYS.autoResizeTable]: LEGACY_KEYS.autoResizeTable,
    [SETTING_KEYS.timeMaxLate]: LEGACY_KEYS.timeMaxLate,
    [SETTING_KEYS.autoAddMaxLate]: LEGACY_KEYS.autoAddMaxLate,
    [SETTING_KEYS.refreshSchedules]: LEGACY_KEYS.refreshSchedules,
    [SETTING_KEYS.tourColumnsSaved]: LEGACY_KEYS.tourColumnsSaved,
    [SETTING_KEYS.hourGroupColumn]: LEGACY_KEYS.hourGroupColumn,
    [SETTING_KEYS.gridColumnState]: LEGACY_KEYS.gridColumnState,
    [SETTING_KEYS.currencyRatesCache]: LEGACY_KEYS.currencyRatesCache,
  };

  for (const [newKey, legacyKey] of Object.entries(settingMap)) {
    const val = getLegacySetting(legacyKey);
    if (val != null) await backend.setSetting(newKey, val);
  }

  const userIds = findLegacyUserIds();
  for (const userId of userIds) {
    // User prefs
    const tableSize = getLegacyUserKey(userId, LEGACY_USER_SUFFIX.tableSize);
    if (tableSize != null) await backend.setUserPref(userId, USER_PREF_KEYS.tableSize, tableSize);

    const isSimpleMode = getLegacyUserKey(userId, LEGACY_USER_SUFFIX.isSimpleMode);
    if (isSimpleMode != null) await backend.setUserPref(userId, USER_PREF_KEYS.isSimpleMode, isSimpleMode);

    const grindSession = getLegacyUserKey(userId, LEGACY_USER_SUFFIX.grindSession);
    if (grindSession != null) await backend.setUserPref(userId, USER_PREF_KEYS.grindSession, grindSession);

    // Play list - array of tournament objects
    const playRaw = getLegacyUserKey(userId, LEGACY_USER_SUFFIX.playList);
    const playList = parseJson<Array<Record<string, unknown>>>(playRaw, []);
    if (Array.isArray(playList) && playList.length > 0) {
      await backend.setPlayList(userId, playList);
    }

    // ID-only lists
    for (const [suffix, listType] of Object.entries(LIST_SUFFIX_TO_TYPE)) {
      const raw = getLegacyUserKey(userId, suffix);
      const arr = parseJson<number[]>(raw, []);
      if (Array.isArray(arr) && arr.length > 0) {
        const ids = arr.map((x) => (typeof x === "number" ? x : Number(x))).filter((n) => !isNaN(n));
        if (ids.length > 0) await backend.setListIds(userId, listType, ids);
      }
    }
  }

  markMigrated();
}
